import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { analysisService } from '../../services/analysisService';
import { userService } from '../../services/userService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import ProgressBar from '../../components/ui/ProgressBar';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faRobot
} from '@fortawesome/free-solid-svg-icons';
import SimplificationModal from '../../components/analysis/SimplificationModal';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import toast from 'react-hot-toast';


export default function AnalysisResultPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [showSimplificationModal, setShowSimplificationModal] = useState(false);
  const [simplificationResult, setSimplificationResult] = useState<{
    originalText: string;
    simplifiedText: string;
    provider: string;
    targetLevel: string;
  } | null>(null);
  const [simplifiedAnalysis, setSimplifiedAnalysis] = useState<any | null>(null);

  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [showingVersion, setShowingVersion] = useState<'original' | 'edited'>('edited'); // Track which version is displayed

  // View mode: simple o expert
  const [_viewMode, _setViewMode] = useState('simple');

  // Estado para validaciones manuales de criterios
  const [criteriaValidations, setCriteriaValidations] = useState<{ [key: string]: boolean | null }>({});

  const { data, isLoading } = useQuery({
    queryKey: ['analysis', id],
    queryFn: () => analysisService.getAnalysisById(Number(id)),
    enabled: !!id,
  });

  // Get user preferences for AI simplification
  const { data: preferencesData } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: () => userService.getPreferences(),
  });

  // Load saved validations from database
  useEffect(() => {
    if (id) {
      analysisService.getCriterionValidations(Number(id))
        .then(result => {
          if (result.validations && Object.keys(result.validations).length > 0) {
            setCriteriaValidations(result.validations);
          }
        })
        .catch(error => {
          console.error('Error loading validations:', error);
        });
    }
  }, [id]);

  // Set initial version to show based on whether edited text exists
  useEffect(() => {
    if (data?.analysis) {
      // If there's edited text, show it by default; otherwise show original
      setShowingVersion(data.analysis.editedText ? 'edited' : 'original');
    }
  }, [data?.analysis]);

  // Mutation for saving validations
  const saveValidationsMutation = useMutation({
    mutationFn: (validations: { [key: string]: boolean | null }) =>
      analysisService.updateCriterionValidations(Number(id), validations),
    onSuccess: () => {
      // Invalidate all grouped analyses queries to refresh history lists
      queryClient.invalidateQueries({ queryKey: ['grouped-analyses'] });
      // Also invalidate the current analysis to ensure it's up-to-date
      queryClient.invalidateQueries({ queryKey: ['analysis', id] });
    },
    onError: (error: any) => {
      console.error('Error saving validations:', error);
      toast.error('Error al guardar las validaciones');
    },
  });

  // Mutation for simplifying full text
  const simplifyMutation = useMutation({
    mutationFn: (text: string) => analysisService.simplifyTextContent({ text }),
    onSuccess: (result) => {
      setSimplificationResult({
        originalText: result.originalText,
        simplifiedText: result.simplifiedText,
        provider: result.provider,
        targetLevel: result.targetLevel,
      });
      setShowSimplificationModal(true);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al simplificar el texto');
    },
  });

  const handleSimplify = () => {
    if (data?.analysis?.originalText) {
      simplifyMutation.mutate(data.analysis.originalText);
    }
  };

  // Mutation for analyzing simplified text
  const analyzeSimplifiedMutation = useMutation({
    mutationFn: (text: string) => analysisService.analyzeText({ text, title: `Simplificación de: ${analysis.title}` }),
    onSuccess: (result) => {
      setSimplifiedAnalysis(result.analysis);
      toast.success('Texto simplificado analizado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al analizar el texto simplificado');
    },
  });

  const handleSimplifyAndAnalyze = () => {
    if (data?.analysis?.originalText) {
      // First simplify
      simplifyMutation.mutate(data.analysis.originalText, {
        onSuccess: async (result) => {
          // Then analyze the simplified text
          analyzeSimplifiedMutation.mutate(result.simplifiedText);
        },
      });
    }
  };

  // Mutation to save edited text and update existing analysis
  const saveEditedTextMutation = useMutation({
    mutationFn: async (text: string) => {
      const analysisId = parseInt(id!);
      return analysisService.updateAnalysisWithEditedText(analysisId, text);
    },
    onSuccess: async (response) => {
      toast.success('Análisis actualizado con el texto editado');

      // Update the cache directly with the new data from the response
      queryClient.setQueryData(['analysis', id], response);

      // Also invalidate to ensure freshness
      await queryClient.invalidateQueries({ queryKey: ['analysis', id] });

      setIsEditMode(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al actualizar el análisis');
    }
  });

  // Mutation for toggling audit status
  const toggleAuditStatusMutation = useMutation({
    mutationFn: async () => {
      const analysisId = parseInt(id!);
      return analysisService.toggleAuditStatus(analysisId);
    },
    onSuccess: async (response) => {
      toast.success(response.message);
      // Update cache directly
      queryClient.setQueryData(['analysis', id], response);
      // Also invalidate to ensure freshness
      await queryClient.invalidateQueries({ queryKey: ['analysis', id] });
      // Invalidate grouped-analyses to update history list badges
      queryClient.invalidateQueries({ queryKey: ['grouped-analyses'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al cambiar el estado de auditoría');
    }
  });

  // Handle entering edit mode
  const handleEnterEditMode = () => {
    setIsEditMode(true);
    // Use edited text if available, otherwise use original text
    setEditedText(data?.analysis?.editedText || data?.analysis?.originalText || '');
  };

  // Handle exiting edit mode
  const handleExitEditMode = () => {
    setIsEditMode(false);
    setEditedText('');
  };

  // Handle save edited text
  const handleSaveEditedText = () => {
    if (editedText.trim().length < 10) {
      toast.error('El texto debe tener al menos 10 caracteres');
      return;
    }
    saveEditedTextMutation.mutate(editedText);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando análisis...</p>
        </div>
      </div>
    );
  }

  if (!data?.analysis) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-muted-foreground mb-4">Análisis no encontrado</p>
          <Button onClick={() => navigate('/history')}>
            Volver al historial
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { analysis } = data;

  // Función para generar acciones recomendadas
  /* UNUSED - Keeping for potential future use
  function _generateActions() {
    const actions: any[] = [];

    // Acción crítica: Glosario
    const glossaryCount = analysis.analysisMetadata?.glossaryTerms?.count || 0;
    if (glossaryCount > 0) {
      actions.push({
        priority: 'critical',
        title: `Añadir glosario para ${glossaryCount} términos`,
        impact: glossaryCount * 4,
        description: `Se detectaron ${glossaryCount} términos C1-C2 que requieren explicación en un glosario para lectores de nivel B2.`,
        examples: analysis.analysisMetadata.glossaryTerms.terms.slice(0, 5),
        detailedInfo: 'Los términos C1-C2 superan el nivel de comprensión de lectores con educación secundaria. Incluir un glosario mejorará significativamente la accesibilidad del documento.',
        actions: [
          {
            label: 'Ver términos completos',
            onClick: () => setActiveTab('glossary'),
            variant: 'default'
          },
          {
            label: 'Ver criterios B2',
            onClick: () => setActiveTab('b2-criteria'),
            variant: 'outline'
          }
        ]
      });
    }

    // Acción importante: Oraciones largas
    if (analysis.avgWordsPerSentence && Number(analysis.avgWordsPerSentence) > 20) {
      const avgWords = Number(analysis.avgWordsPerSentence).toFixed(1);
      actions.push({
        priority: 'important',
        title: 'Dividir oraciones largas',
        impact: 15,
        description: `El promedio es de ${avgWords} palabras por oración. Se recomienda un máximo de 15-20 palabras.`,
        examples: [
          `Promedio actual: ${avgWords} palabras/oración`,
          'Máximo recomendado: 15-20 palabras/oración'
        ],
        detailedInfo: 'Las oraciones largas dificultan la comprensión. Divídelas en oraciones más cortas y directas para mejorar la claridad.',
        actions: [
          {
            label: 'Ver análisis completo',
            onClick: () => setActiveTab('b2-criteria')
          }
        ]
      });
    }

    // Acción importante: Voz pasiva
    const passiveVoiceCount = analysis.analysisMetadata?.b2Criteria?.passiveVoice?.count || 0;
    if (passiveVoiceCount > 0) {
      actions.push({
        priority: 'important',
        title: 'Convertir voz pasiva a voz activa',
        impact: 12,
        description: `Se detectaron ${passiveVoiceCount} construcciones en voz pasiva. La voz activa es más directa y clara.`,
        examples: analysis.analysisMetadata.b2Criteria.passiveVoice.examples.slice(0, 3),
        detailedInfo: 'La voz activa hace el texto más dinámico y fácil de entender, indicando claramente quién realiza la acción.',
        actions: [
          {
            label: 'Ver ejemplos',
            onClick: () => setActiveTab('b2-criteria')
          }
        ]
      });
    }

    // Acción opcional: Conectores complejos
    const complexConnectorsCount = analysis.analysisMetadata?.b2Criteria?.complexConnectors?.count || 0;
    if (complexConnectorsCount > 0) {
      actions.push({
        priority: 'optional',
        title: 'Simplificar conectores',
        impact: 8,
        description: `Se detectaron ${complexConnectorsCount} conectores complejos. Usa conectores más simples para mejorar la fluidez.`,
        examples: analysis.analysisMetadata.b2Criteria.complexConnectors.connectors.slice(0, 5),
        detailedInfo: 'Los conectores simples como "pero", "porque", "entonces" son más fáciles de entender que "no obstante", "en virtud de", etc.',
        actions: [
          {
            label: 'Ver detalles',
            onClick: () => setActiveTab('b2-criteria')
          }
        ]
      });
    }

    // Acción opcional: Palabras vagas
    const vagueWordsCount = analysis.analysisMetadata?.b2Criteria?.vagueWords?.count || 0;
    if (vagueWordsCount > 5) {
      actions.push({
        priority: 'optional',
        title: 'Reemplazar palabras vagas',
        impact: 6,
        description: `Se detectaron ${vagueWordsCount} palabras imprecisas. Usa términos más específicos y concretos.`,
        examples: analysis.analysisMetadata.b2Criteria.vagueWords.words.slice(0, 5),
        detailedInfo: 'Palabras como "cosa", "tema", "asunto" son vagas. Usa palabras específicas que el lector pueda visualizar claramente.',
        actions: [
          {
            label: 'Ver detalles',
            onClick: () => setActiveTab('b2-criteria')
          }
        ]
      });
    }

    return actions;
  }
  */

  // Tab: Resumen General
  function renderOverviewTab() {
    /* UNUSED - Keeping for potential future use
    // Calculate dominant vocabulary level
    const vocabularyDistribution = analysis.analysisMetadata?.vocabularyDistribution;

    const dominantLevel = vocabularyDistribution
      ? Object.entries(vocabularyDistribution)
          .sort((a, b) => (b[1] as number) - (a[1] as number))[0]
      : null;

    const _dominantLevelName = dominantLevel?.[0] || 'N/A';
    const _dominantLevelPercentage = dominantLevel?.[1] ? (dominantLevel[1] as number).toFixed(1) : '0';

    // Get INFLESZ interpretation
    const infleszScore = analysis.infleszScore != null ? Number(analysis.infleszScore) : null;
    const _infleszInterpretation = infleszScore != null && infleszScore > 0
      ? infleszScore >= 80 ? 'Muy fácil - Nivel A1/A2'
      : infleszScore >= 65 ? 'Fácil - Nivel B1/B2'
      : infleszScore >= 55 ? 'Normal - Nivel B2'
      : infleszScore >= 40 ? 'Difícil - Nivel C1'
      : 'Muy difícil - Nivel C2'
      : analysis.totalWords && analysis.totalWords < 20
      ? 'Texto corto - Adecuado'
      : 'No disponible';
    */

    return (
      <div className="space-y-6">
        {/* Texto Original */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Texto Analizado</h3>
              <div className="flex items-center gap-2">
                {!isEditMode && (
                  <Button
                    onClick={handleEnterEditMode}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faFileAlt} />
                    Editar Texto
                  </Button>
                )}
                {isEditMode && (
                  <>
                    <Button
                      onClick={handleSaveEditedText}
                      disabled={saveEditedTextMutation.isPending}
                      size="sm"
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {saveEditedTextMutation.isPending ? 'Analizando...' : 'Guardar y Analizar'}
                    </Button>
                    <Button
                      onClick={handleExitEditMode}
                      disabled={saveEditedTextMutation.isPending}
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      Cancelar Edición
                    </Button>
                  </>
                )}
                {preferencesData?.preferences?.enableAiSimplification && !isEditMode && (
                  <Button
                    onClick={handleSimplify}
                    disabled={simplifyMutation.isPending}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faRobot} />
                    {simplifyMutation.isPending ? 'Simplificando...' : 'Simplificar con IA'}
                  </Button>
                )}
              </div>
            </div>

            {!isEditMode ? (
              <>
                {/* Version history controls - only show if there's an edited version */}
                {analysis.editedText && (
                  <div className="mb-3 flex items-center justify-between gap-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-blue-900">Versión:</span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={showingVersion === 'edited' ? 'default' : 'outline'}
                          onClick={() => setShowingVersion('edited')}
                          className="text-xs"
                        >
                          Editada (Actual)
                        </Button>
                        <Button
                          size="sm"
                          variant={showingVersion === 'original' ? 'default' : 'outline'}
                          onClick={() => setShowingVersion('original')}
                          className="text-xs"
                        >
                          Original
                        </Button>
                      </div>
                      <span className="text-xs text-blue-700">
                        {showingVersion === 'edited' ? '(Mostrando versión editada - análisis actualizado)' : '(Mostrando versión original)'}
                      </span>
                    </div>
                    {showingVersion === 'original' && (
                      <Button
                        size="sm"
                        onClick={() => saveEditedTextMutation.mutate(analysis.originalText)}
                        disabled={saveEditedTextMutation.isPending}
                        className="text-xs bg-green-600 hover:bg-green-700"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                        {saveEditedTextMutation.isPending ? 'Analizando...' : 'Volver a analizar original'}
                      </Button>
                    )}
                  </div>
                )}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                  <div
                    className="prose prose-sm max-w-none formatted-content"
                    dangerouslySetInnerHTML={{
                      __html: showingVersion === 'edited' && analysis.editedText
                        ? analysis.editedText
                        : analysis.originalText
                    }}
                  />
                </div>
              </>
            ) : (
              <RichTextEditor
                content={editedText}
                onChange={(content) => setEditedText(content)}
                placeholder="Escribe o edita tu texto aquí..."
              />
            )}

            {/* CSS Styles for formatted content display */}
            <style>{`
              .formatted-content ul,
              .formatted-content ol {
                padding-left: 1.5rem;
                margin: 0.5rem 0;
              }

              .formatted-content ul {
                list-style-type: disc;
              }

              .formatted-content ol {
                list-style-type: decimal;
              }

              .formatted-content li {
                margin: 0.25rem 0;
              }

              .formatted-content p {
                margin: 0.5rem 0;
              }

              .formatted-content strong {
                font-weight: bold;
              }

              .formatted-content em {
                font-style: italic;
              }

              .formatted-content u {
                text-decoration: underline;
              }

              .formatted-content [style*="text-align: center"] {
                text-align: center;
              }

              .formatted-content [style*="text-align: right"] {
                text-align: right;
              }

              .formatted-content [style*="text-align: justify"] {
                text-align: justify;
              }
            `}</style>

            <div className="mt-3 text-xs text-gray-500">
              {analysis.totalWords} palabras • {analysis.totalSentences} oraciones
            </div>
          </CardContent>
        </Card>

        {/* Evaluación según UNE-ISO 24495-1:2024 */}
        {analysis.analysisMetadata?.b2Criteria && (() => {
          const b2Data = analysis.analysisMetadata.b2Criteria;
          const allCriteria = b2Data.criteria || [];

          // Clasificar criterios por principio UNE (misma lógica que en renderB2CriteriaTab)
          const pertinenciaCriteria = allCriteria.filter((c: any) =>
            c.name.toLowerCase().includes('adecuación al público') ||
            c.name.toLowerCase().includes('propósito claro') ||
            c.name.toLowerCase().includes('suficiencia') ||
            c.name.toLowerCase().includes('superflua')
          );

          const facilidadBusquedaCriteria = allCriteria.filter((c: any) =>
            (c.name.toLowerCase().includes('estructura clara') && c.name.toLowerCase().includes('títulos')) ||
            c.name.toLowerCase().includes('listas') ||
            c.name.toLowerCase().includes('viñetas') ||
            c.name.toLowerCase().includes('ilustraciones') ||
            (c.name.toLowerCase().includes('gráficos') && c.name.toLowerCase().includes('iconos'))
          );

          const comprensibilidadCriteria = allCriteria.filter((c: any) => {
            if (c.name.toLowerCase().includes('listas') ||
                c.name.toLowerCase().includes('viñetas') ||
                c.name.toLowerCase().includes('ilustraciones') ||
                c.name.toLowerCase().includes('gráficos') ||
                (c.name.toLowerCase().includes('iconos') && c.name.toLowerCase().includes('glosario'))) {
              return false;
            }

            return (
              ['legibilidad', 'vocabulario', 'claridad'].includes(c.category?.toLowerCase()) ||
              (c.category?.toLowerCase() === 'estructura' &&
               (c.name.toLowerCase().includes('voz activa') ||
                c.name.toLowerCase().includes('conectores') ||
                c.name.toLowerCase().includes('subordinadas'))) ||
              (c.category?.toLowerCase() === 'formato' && c.name.toLowerCase().includes('mayúsculas')) ||
              c.name.toLowerCase().includes('inflesz') ||
              c.name.toLowerCase().includes('glosario') ||
              c.name.toLowerCase().includes('negaciones') ||
              c.name.toLowerCase().includes('indeterminadas') ||
              c.name.toLowerCase().includes('tono y estilo')
            );
          });

          const usabilidadCriteria = allCriteria.filter((c: any) =>
            c.name.toLowerCase().includes('validación') ||
            c.name.toLowerCase().includes('usuarios reales') ||
            c.name.toLowerCase().includes('orientación a la acción') ||
            c.name.toLowerCase().includes('siguientes pasos')
          );

          // Calcular estado de cada principio (usando pesos para Comprensibilidad)
          const calculatePrincipleStatus = (criteria: any[], useWeights: boolean = false) => {
            if (criteria.length === 0) return { passes: false, percentage: 0, validated: 0, total: 0 };

            if (useWeights) {
              let earnedScore = 0;
              let maxScore = 0;

              criteria.forEach((criterion, index) => {
                const criterionKey = `${criterion.name}-${index}`;
                const validation = criteriaValidations[criterionKey] !== undefined
                  ? criteriaValidations[criterionKey]
                  : (!criterion.scientific ? null : criterion.status === 'pass');

                const weight = criterion.weight || 0;
                maxScore += weight;

                if (validation === true) {
                  earnedScore += weight;
                }
              });

              const percentage = maxScore > 0 ? (earnedScore / maxScore) * 100 : 0;
              return {
                passes: percentage >= 85,
                percentage: percentage,
                validated: Math.round(earnedScore),
                total: maxScore
              };
            } else {
              let passedCount = 0;
              let totalCriteria = criteria.length;

              criteria.forEach((criterion, index) => {
                const criterionKey = `${criterion.name}-${index}`;
                const validation = criteriaValidations[criterionKey] !== undefined
                  ? criteriaValidations[criterionKey]
                  : (!criterion.scientific ? null : criterion.status === 'pass');

                if (validation === true) {
                  passedCount++;
                }
              });

              const percentage = (passedCount / totalCriteria) * 100;
              return {
                passes: percentage >= 85,
                percentage: percentage,
                validated: passedCount,
                total: totalCriteria
              };
            }
          };

          // Calcular estados
          // Añadir los 3 criterios manuales de Pertinencia
          const manualPertinenciaCriteria = [
            {
              name: 'Adecuación al Público Objetivo',
              category: 'pertinencia',
              description: 'Verificar que el documento se ha escrito pensando en el perfil del lector',
              scientific: false,
              status: null
            },
            {
              name: 'Propósito Claro',
              category: 'pertinencia',
              description: 'Evaluar si el documento responde directamente al objetivo del lector',
              scientific: false,
              status: null
            },
            {
              name: 'Suficiencia de la Información',
              category: 'pertinencia',
              description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo',
              scientific: false,
              status: null
            }
          ];
          const pertinenciaStatus = calculatePrincipleStatus([...manualPertinenciaCriteria, ...pertinenciaCriteria], false);
          const facilidadStatus = facilidadBusquedaCriteria.length > 0
            ? calculatePrincipleStatus(facilidadBusquedaCriteria, false)
            : { passes: true, percentage: 100, validated: 0, total: 0 };
          const comprensibilidadStatus = calculatePrincipleStatus(comprensibilidadCriteria, true);
          const usabilidadStatus = usabilidadCriteria.length > 0
            ? calculatePrincipleStatus(usabilidadCriteria, false)
            : { passes: true, percentage: 100, validated: 0, total: 0 };

          const cumpleLenguajeClaro = pertinenciaStatus.passes && facilidadStatus.passes && comprensibilidadStatus.passes && usabilidadStatus.passes;

          return (
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">Evaluación según UNE-ISO 24495-1:2024</h3>
                    <p className="text-sm text-gray-600">Criterios organizados por los 4 principios de Lenguaje Claro</p>
                  </div>
                  <div className={`px-6 py-3 rounded-lg text-sm font-bold border-2 ${
                    cumpleLenguajeClaro
                      ? 'bg-green-100 text-green-800 border-green-400'
                      : 'bg-red-100 text-red-800 border-red-400'
                  }`}>
                    {cumpleLenguajeClaro ? '✓ CUMPLE LENGUAJE CLARO' : '✗ NO CUMPLE LENGUAJE CLARO'}
                  </div>
                </div>

                {/* Grid de 4 principios */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Principio 1: Pertinencia */}
                  <div className={`p-4 rounded-lg border-2 ${
                    pertinenciaStatus.passes
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-800">Principio 1. Pertinencia</h4>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${
                        pertinenciaStatus.passes
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800'
                      }`}>
                        {pertinenciaStatus.passes ? '✓ PASA' : '✗ NO PASA'}
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          pertinenciaStatus.passes
                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                            : 'bg-gradient-to-r from-red-500 to-orange-500'
                        }`}
                        style={{ width: `${Math.min(pertinenciaStatus.percentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {pertinenciaStatus.validated}/{pertinenciaStatus.total} criterios ({pertinenciaStatus.percentage.toFixed(0)}%)
                    </p>
                  </div>

                  {/* Principio 2: Facilidad de Búsqueda */}
                  <div className={`p-4 rounded-lg border-2 ${
                    facilidadStatus.passes
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-800">Principio 2. Facilidad de Búsqueda</h4>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${
                        facilidadStatus.passes
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800'
                      }`}>
                        {facilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'}
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          facilidadStatus.passes
                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                            : 'bg-gradient-to-r from-red-500 to-orange-500'
                        }`}
                        style={{ width: `${Math.min(facilidadStatus.percentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {facilidadStatus.validated}/{facilidadStatus.total} criterios ({facilidadStatus.percentage.toFixed(0)}%)
                    </p>
                  </div>

                  {/* Principio 3: Comprensibilidad */}
                  <div className={`p-4 rounded-lg border-2 ${
                    comprensibilidadStatus.passes
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-800">Principio 3. Comprensibilidad (B2)</h4>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${
                        comprensibilidadStatus.passes
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800'
                      }`}>
                        {comprensibilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'}
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          comprensibilidadStatus.passes
                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                            : 'bg-gradient-to-r from-red-500 to-orange-500'
                        }`}
                        style={{ width: `${Math.min(comprensibilidadStatus.percentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Ponderado: {comprensibilidadStatus.percentage.toFixed(0)}% ({comprensibilidadStatus.validated}/{comprensibilidadStatus.total})
                    </p>
                  </div>

                  {/* Principio 4: Usabilidad */}
                  <div className={`p-4 rounded-lg border-2 ${
                    usabilidadStatus.passes
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-800">Principio 4. Usabilidad</h4>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${
                        usabilidadStatus.passes
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800'
                      }`}>
                        {usabilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'}
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          usabilidadStatus.passes
                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                            : 'bg-gradient-to-r from-red-500 to-orange-500'
                        }`}
                        style={{ width: `${Math.min(usabilidadStatus.percentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {usabilidadStatus.validated}/{usabilidadStatus.total} criterios ({usabilidadStatus.percentage.toFixed(0)}%)
                    </p>
                  </div>
                </div>

                {/* Mensaje informativo */}
                <div className="mt-4 p-3 bg-blue-100 border border-blue-300 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Umbral requerido:</strong> Para cumplir con Lenguaje Claro según UNE-ISO 24495-1:2024,
                    cada uno de los 4 principios debe alcanzar al menos el 85% de cumplimiento.
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Términos que Requieren Glosario (REQUERIDO) */}
        {analysis.analysisMetadata?.glossaryTerms && analysis.analysisMetadata.glossaryTerms.count > 0 && (
          <Card className="border-2 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">📖</span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800">Términos que Requieren Glosario</h3>
                  <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs font-bold rounded mt-1">
                    REQUERIDO
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Términos especializados de la base de datos que REQUIEREN definición en glosario para ser comprendidos por lectores con nivel básico-intermedio.
              </p>

              {/* Estadísticas */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div>
                  <h4 className="text-orange-800 font-semibold mb-1">
                    {analysis.analysisMetadata.glossaryTerms.count} términos detectados
                  </h4>
                  {analysis.analysisMetadata.glossaryTerms.breakdown && (
                    <p className="text-sm text-orange-700">
                      {analysis.analysisMetadata.glossaryTerms.breakdown.c1Words} palabras C1 • {' '}
                      {analysis.analysisMetadata.glossaryTerms.breakdown.c2Words} palabras C2
                    </p>
                  )}
                </div>
              </div>

              {/* Lista de términos */}
              <div>
                <h4 className="font-semibold mb-3 text-gray-700">Términos Detectados</h4>
                <div className="flex flex-wrap gap-2">
                  {analysis.analysisMetadata.glossaryTerms.terms.map((term: string, i: number) => (
                    <span key={i} className="px-3 py-2 bg-orange-100 text-orange-900 rounded-lg text-sm font-medium border border-orange-200">
                      {term}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vocabulario C1-C2 para Simplificar (PRIORIDAD ALTA) */}
        {analysis.analysisMetadata?.c1c2Vocabulary && analysis.analysisMetadata.c1c2Vocabulary.count > 0 && (
          <Card className="border-2 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">⚠️</span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800">Vocabulario C1-C2 para Simplificar</h3>
                  <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded mt-1">
                    PRIORIDAD ALTA
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {analysis.analysisMetadata.c1c2Vocabulary.recommendation}
              </p>

              {/* Estadísticas */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div>
                  <h4 className="text-red-800 font-semibold mb-1">
                    {analysis.analysisMetadata.c1c2Vocabulary.count} palabras deben simplificarse
                  </h4>
                  <p className="text-sm text-red-700">
                    Estas palabras técnicas del corpus deben reemplazarse por alternativas más sencillas
                  </p>
                </div>
              </div>

              {/* Lista de palabras */}
              <div>
                <h4 className="font-semibold mb-3 text-gray-700">Palabras Detectadas</h4>
                <div className="flex flex-wrap gap-2">
                  {analysis.analysisMetadata.c1c2Vocabulary.words.map((word: string, i: number) => (
                    <span key={i} className="px-3 py-2 bg-red-100 text-red-900 rounded-lg text-sm font-medium border border-red-200">
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Palabras Complejas (Morfología) - PRIORIDAD MEDIA */}
        {analysis.analysisMetadata?.complexWords && analysis.analysisMetadata.complexWords.total > 0 && (
          <Card className="border-2 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-3">
                <span className="text-2xl">🔤</span>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800">Palabras Complejas (Análisis Morfológico)</h3>
                  <span className="inline-block px-2 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded mt-1">
                    PRIORIDAD MEDIA
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Palabras detectadas como complejas mediante análisis morfológico, estructura silábica (CVCCV) y longitud.
                Se recomienda simplificar con prioridad media/baja.
              </p>

              {/* Estadísticas */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div>
                  <h4 className="text-amber-800 font-semibold mb-1">
                    {analysis.analysisMetadata.complexWords.total} palabras complejas detectadas
                  </h4>
                  <p className="text-sm text-amber-700">
                    {analysis.analysisMetadata.complexWords.percentage.toFixed(1)}% del texto total
                  </p>
                </div>
              </div>

              {/* Lista de palabras */}
              {analysis.analysisMetadata.complexWords.words && analysis.analysisMetadata.complexWords.words.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 text-gray-700">Palabras Detectadas ({analysis.analysisMetadata.complexWords.words.length})</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.analysisMetadata.complexWords.words.map((wordInfo: any, i: number) => (
                      <span
                        key={i}
                        className="px-3 py-2 bg-amber-100 text-amber-900 rounded-lg text-sm font-medium border border-amber-200"
                        title={wordInfo.reason || ''}
                      >
                        {wordInfo.word || wordInfo}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Tab: Recomendaciones
  function renderRecommendationsTab() {
    if (!analysis.analysisMetadata?.b2Criteria) {
      return <div className="text-center text-gray-500 py-8">No hay datos disponibles para generar recomendaciones</div>;
    }

    const recommendations: Array<{
      priority: 'high' | 'medium' | 'low';
      title: string;
      description: string;
      actions: string[];
      examples?: string[];
      category: string;
    }> = [];

    const b2Data = analysis.analysisMetadata.b2Criteria;
    const allCriteria = b2Data.criteria || [];

    // Añadir los 3 criterios manuales de Pertinencia
    const manualPertinenciaCriteria = [
      {
        name: 'Adecuación al Público Objetivo',
        category: 'pertinencia',
        description: 'Verificar que el documento se ha escrito pensando en el perfil del lector | Evaluar conocimientos previos, contexto sociocultural y necesidades específicas del público objetivo',
        scientific: false,
        status: null
      },
      {
        name: 'Propósito Claro',
        category: 'pertinencia',
        description: 'Evaluar si el documento responde directamente al objetivo del lector | El lector debe poder tomar una decisión, realizar un trámite o entender un concepto específico',
        scientific: false,
        status: null
      },
      {
        name: 'Suficiencia de la Información',
        category: 'pertinencia',
        description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo | Ni más ni menos: solo lo necesario para cumplir el propósito sin generar ruido informativo',
        scientific: false,
        status: null
      }
    ];

    // Clasificar criterios por principio UNE
    const pertinenciaCriteria = [
      ...manualPertinenciaCriteria,
      ...allCriteria.filter((c: any) =>
        c.name.toLowerCase().includes('adecuación al público') ||
        c.name.toLowerCase().includes('propósito claro') ||
        c.name.toLowerCase().includes('suficiencia') ||
        c.name.toLowerCase().includes('superflua')
      )
    ];

    const facilidadBusquedaCriteria = allCriteria.filter((c: any) =>
      (c.name.toLowerCase().includes('estructura clara') && c.name.toLowerCase().includes('títulos')) ||
      c.name.toLowerCase().includes('listas') ||
      c.name.toLowerCase().includes('viñetas') ||
      c.name.toLowerCase().includes('ilustraciones') ||
      (c.name.toLowerCase().includes('gráficos') && c.name.toLowerCase().includes('iconos'))
    );

    const comprensibilidadCriteria = allCriteria.filter((c: any) => {
      if (c.name.toLowerCase().includes('listas') ||
          c.name.toLowerCase().includes('viñetas') ||
          c.name.toLowerCase().includes('ilustraciones') ||
          c.name.toLowerCase().includes('gráficos') ||
          (c.name.toLowerCase().includes('iconos') && c.name.toLowerCase().includes('glosario'))) {
        return false;
      }

      return (
        ['legibilidad', 'vocabulario', 'claridad'].includes(c.category?.toLowerCase()) ||
        (c.category?.toLowerCase() === 'estructura' &&
         (c.name.toLowerCase().includes('voz activa') ||
          c.name.toLowerCase().includes('conectores') ||
          c.name.toLowerCase().includes('subordinadas'))) ||
        (c.category?.toLowerCase() === 'formato' && c.name.toLowerCase().includes('mayúsculas')) ||
        c.name.toLowerCase().includes('inflesz') ||
        c.name.toLowerCase().includes('glosario') ||
        c.name.toLowerCase().includes('negaciones') ||
        c.name.toLowerCase().includes('indeterminadas') ||
        c.name.toLowerCase().includes('tono y estilo')
      );
    });

    const usabilidadCriteria = allCriteria.filter((c: any) =>
      c.name.toLowerCase().includes('validación') ||
      c.name.toLowerCase().includes('usuarios reales') ||
      c.name.toLowerCase().includes('orientación a la acción') ||
      c.name.toLowerCase().includes('siguientes pasos')
    );

    // Combinar todos los criterios incluyendo manuales
    const allCriteriaWithManual = [...pertinenciaCriteria, ...facilidadBusquedaCriteria, ...comprensibilidadCriteria, ...usabilidadCriteria];

    // Analizar cada criterio que no pasa y generar recomendaciones
    allCriteriaWithManual.forEach((criterion: any, index: number) => {
      const criterionKey = `${criterion.name}-${index}`;
      const validation = criteriaValidations[criterionKey] !== undefined
        ? criteriaValidations[criterionKey]
        : (!criterion.scientific ? null : criterion.status === 'pass');

      // Solo generar recomendaciones para criterios que NO pasan
      if (validation === false) {
        // Determinar categoría del principio
        let category = '';
        if (pertinenciaCriteria.some((c: any) => c.name === criterion.name)) category = 'Pertinencia';
        else if (facilidadBusquedaCriteria.some((c: any) => c.name === criterion.name)) category = 'Facilidad de Búsqueda';
        else if (comprensibilidadCriteria.some((c: any) => c.name === criterion.name)) category = 'Comprensibilidad';
        else if (usabilidadCriteria.some((c: any) => c.name === criterion.name)) category = 'Usabilidad';

        // Generar recomendación específica según el criterio
        if (criterion.name.toLowerCase().includes('adecuación al público')) {
          recommendations.push({
            priority: 'high',
            title: 'Adaptar el contenido al público objetivo',
            description: 'El documento debe estar escrito pensando en el perfil específico del lector.',
            actions: [
              'Identifica el nivel de conocimientos previos de tu audiencia',
              'Adapta el vocabulario y complejidad al perfil del lector',
              'Considera el contexto sociocultural del público objetivo',
              'Evita dar por sentado conocimientos que el lector puede no tener',
              'Incluye ejemplos o analogías relevantes para tu audiencia'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('propósito claro')) {
          recommendations.push({
            priority: 'high',
            title: 'Definir y comunicar el propósito del documento',
            description: 'El documento debe responder directamente al objetivo del lector.',
            actions: [
              'Define claramente qué acción debe poder realizar el lector',
              'Incluye una introducción que explique el propósito del documento',
              'Organiza el contenido en función del objetivo principal',
              'Asegúrate de que cada sección contribuya al propósito general',
              'Concluye con pasos concretos o información clave para actuar'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('suficiencia')) {
          recommendations.push({
            priority: 'medium',
            title: 'Ajustar la cantidad de información',
            description: 'El documento debe contener toda la información necesaria sin incluir contenido superfluo.',
            actions: [
              'Revisa cada sección y elimina información redundante',
              'Verifica que no falte información clave para cumplir el propósito',
              'Evita desvíos del tema principal',
              'Prioriza calidad sobre cantidad: "ni más ni menos"',
              'Si hay información adicional útil, considérala como anexo'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('inflesz')) {
          const infleszScore = typeof analysis.infleszScore === 'number' ? analysis.infleszScore : 0;
          recommendations.push({
            priority: 'high',
            title: 'Mejorar legibilidad del texto (INFLESZ)',
            description: `El índice INFLESZ es ${infleszScore.toFixed(1)}. Un valor por encima de 55 indica texto muy fácil de leer.`,
            actions: [
              'Acorta las oraciones (máximo 15-20 palabras)',
              'Usa palabras más simples y comunes',
              'Evita vocabulario técnico sin explicación',
              'Divide párrafos largos en secciones más pequeñas'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('palabras por oración')) {
          const avgWords = analysis.avgWordsPerSentence ? Number(analysis.avgWordsPerSentence).toFixed(1) : 'N/A';
          const longSentences = b2Data.longSentences || [];
          recommendations.push({
            priority: 'high',
            title: 'Reducir longitud de oraciones',
            description: `El promedio es ${avgWords} palabras/oración. Se recomienda máximo 15-20 palabras.`,
            actions: [
              'Divide oraciones largas en dos o más oraciones cortas',
              'Elimina información redundante o secundaria',
              'Usa puntos en lugar de comas para separar ideas',
              'Revisa las oraciones que superan las 25 palabras'
            ],
            examples: longSentences.slice(0, 3).map((s: any) => `"${s.text}" (${s.wordCount} palabras)`),
            category
          });
        } else if (criterion.name.toLowerCase().includes('glosario') || criterion.name.toLowerCase().includes('términos técnicos')) {
          const glossaryTerms = analysis.analysisMetadata?.glossaryTerms?.terms || [];
          recommendations.push({
            priority: 'high',
            title: 'Agregar glosario de términos',
            description: `Se detectaron ${glossaryTerms.length} términos técnicos o especializados (niveles C1-C2).`,
            actions: [
              'Crea un glosario al final del documento',
              'Define cada término técnico de forma simple',
              'Vincula los términos en el texto al glosario',
              'Considera usar sinónimos más simples cuando sea posible'
            ],
            examples: glossaryTerms.slice(0, 5),
            category
          });
        } else if (criterion.name.toLowerCase().includes('voz activa')) {
          const passiveVoiceCount = b2Data.passiveVoice?.count || 0;
          const passiveExamples = b2Data.passiveVoice?.examples || [];
          recommendations.push({
            priority: 'medium',
            title: 'Convertir voz pasiva a voz activa',
            description: `Se detectaron ${passiveVoiceCount} construcciones en voz pasiva. La voz activa es más directa.`,
            actions: [
              'Identifica quién realiza la acción',
              'Coloca al sujeto al inicio de la oración',
              'Usa verbos en forma activa',
              'Ejemplo: Cambia "El informe fue elaborado por el equipo" por "El equipo elaboró el informe"'
            ],
            examples: passiveExamples.slice(0, 3),
            category
          });
        } else if (criterion.name.toLowerCase().includes('conectores')) {
          const complexConnectors = b2Data.complexConnectors?.connectors || [];
          recommendations.push({
            priority: 'medium',
            title: 'Simplificar conectores',
            description: `Se detectaron ${complexConnectors.length} conectores complejos.`,
            actions: [
              'Reemplaza "no obstante" por "pero" o "sin embargo"',
              'Usa "porque" en lugar de "en virtud de" o "debido a que"',
              'Cambia "por consiguiente" por "entonces" o "por eso"',
              'Prefiere "aunque" a "si bien" o "pese a que"'
            ],
            examples: complexConnectors.slice(0, 5),
            category
          });
        } else if (criterion.name.toLowerCase().includes('subordinadas')) {
          const subordinateCount = b2Data.subordinateClauses?.count || 0;
          recommendations.push({
            priority: 'medium',
            title: 'Reducir oraciones subordinadas',
            description: `Se detectaron ${subordinateCount} oraciones con múltiples subordinadas.`,
            actions: [
              'Divide oraciones complejas en oraciones simples',
              'Limita el uso de "que", "cual", "donde", "cuando" en una misma oración',
              'Evita encadenar más de dos ideas con comas',
              'Prioriza la claridad sobre la elegancia sintáctica'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('mayúsculas')) {
          const uppercaseCount = b2Data.uppercaseWords?.count || 0;
          recommendations.push({
            priority: 'low',
            title: 'Reducir uso de mayúsculas',
            description: `Se detectaron ${uppercaseCount} palabras o frases en mayúsculas completas.`,
            actions: [
              'Usa mayúsculas solo para nombres propios y siglas',
              'Evita escribir palabras completas en mayúsculas (dificulta lectura)',
              'Para enfatizar, usa negritas en lugar de mayúsculas',
              'Las mayúsculas transmiten agresividad en textos digitales'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('negaciones')) {
          const doubleNegations = b2Data.doubleNegations?.count || 0;
          recommendations.push({
            priority: 'medium',
            title: 'Eliminar dobles negaciones',
            description: `Se detectaron ${doubleNegations} construcciones con doble negación.`,
            actions: [
              'Reescribe en forma afirmativa siempre que sea posible',
              'Ejemplo: Cambia "No es imposible" por "Es posible"',
              'Cambia "No está incompleto" por "Está completo"',
              'Las afirmaciones son más claras que las dobles negaciones'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('palabras vagas') || criterion.name.toLowerCase().includes('indeterminadas')) {
          const vagueWords = b2Data.vagueWords?.words || [];
          recommendations.push({
            priority: 'low',
            title: 'Reemplazar palabras vagas',
            description: `Se detectaron ${vagueWords.length} palabras imprecisas o vagas.`,
            actions: [
              'Reemplaza "cosa" por el término específico (documento, producto, servicio)',
              'Cambia "tema" por el nombre concreto del asunto',
              'Usa números exactos en lugar de "varios", "algunos", "muchos"',
              'Especifica fechas en lugar de "próximamente" o "pronto"'
            ],
            examples: vagueWords.slice(0, 5),
            category
          });
        } else if (criterion.name.toLowerCase().includes('estructura clara') || criterion.name.toLowerCase().includes('títulos')) {
          recommendations.push({
            priority: 'high',
            title: 'Mejorar estructura y títulos',
            description: 'El documento necesita una estructura clara con títulos descriptivos.',
            actions: [
              'Organiza el contenido con títulos y subtítulos',
              'Usa títulos descriptivos que indiquen el contenido',
              'Crea una jerarquía visual clara (H1, H2, H3)',
              'Añade un índice al inicio si el documento es extenso'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('listas') || criterion.name.toLowerCase().includes('viñetas')) {
          recommendations.push({
            priority: 'medium',
            title: 'Usar listas y viñetas',
            description: 'Las listas facilitan la lectura y comprensión de información secuencial.',
            actions: [
              'Convierte series de pasos en listas numeradas',
              'Usa viñetas para enumerar elementos sin orden específico',
              'Mantén los elementos de la lista paralelos en estructura',
              'Limita cada lista a 7-10 elementos máximo'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('ilustraciones') || criterion.name.toLowerCase().includes('gráficos')) {
          recommendations.push({
            priority: 'low',
            title: 'Agregar elementos visuales',
            description: 'Los elementos visuales ayudan a comprender información compleja.',
            actions: [
              'Añade diagramas para explicar procesos',
              'Usa gráficos para mostrar datos numéricos',
              'Incluye iconos para destacar información clave',
              'Asegúrate de que las imágenes tengan texto alternativo'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('validación') || criterion.name.toLowerCase().includes('usuarios reales')) {
          recommendations.push({
            priority: 'medium',
            title: 'Validar con usuarios reales',
            description: 'Es necesario validar el documento con usuarios del público objetivo.',
            actions: [
              'Realiza pruebas de usabilidad con 5-8 usuarios',
              'Pide a usuarios que completen tareas usando el documento',
              'Observa dónde tienen dificultades o confusiones',
              'Itera el documento basándote en el feedback recibido'
            ],
            category
          });
        } else if (criterion.name.toLowerCase().includes('orientación a la acción') || criterion.name.toLowerCase().includes('siguientes pasos')) {
          recommendations.push({
            priority: 'medium',
            title: 'Indicar siguientes pasos',
            description: 'El documento debe indicar claramente qué debe hacer el lector.',
            actions: [
              'Añade una sección de "Próximos pasos" o "Qué hacer ahora"',
              'Usa verbos de acción claros (completa, envía, contacta)',
              'Proporciona enlaces o contactos necesarios',
              'Indica plazos o fechas límite cuando sea relevante'
            ],
            category
          });
        } else {
          // Recomendación genérica para otros criterios
          recommendations.push({
            priority: 'medium',
            title: `Revisar: ${criterion.name}`,
            description: criterion.description || 'Este criterio requiere atención.',
            actions: [
              'Revisa el criterio en la pestaña "Criterios"',
              'Consulta la normativa UNE-ISO 24495-1:2024',
              'Aplica las mejores prácticas de lenguaje claro',
              'Considera consultar con un experto en comunicación clara'
            ],
            category
          });
        }
      }
    });

    // Ordenar recomendaciones por prioridad
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    recommendations.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-bold text-blue-900 mb-2">📋 Recomendaciones para Mejorar</h3>
          <p className="text-sm text-blue-800">
            Esta pestaña muestra tareas concretas para mejorar los criterios que no cumplen con Lenguaje Claro.
            {recommendations.length === 0 && ' ¡Excelente! No hay recomendaciones pendientes.'}
          </p>
        </div>

        {recommendations.length === 0 ? (
          <Card className="bg-green-50 border-2 border-green-200">
            <CardContent className="p-6 text-center">
              <div className="text-6xl mb-4">✓</div>
              <h3 className="text-xl font-bold text-green-800 mb-2">
                ¡Felicitaciones!
              </h3>
              <p className="text-green-700">
                Todos los criterios evaluados cumplen con los estándares de Lenguaje Claro.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Resumen de recomendaciones */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-red-50 border-2 border-red-200">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-red-600">
                    {recommendations.filter(r => r.priority === 'high').length}
                  </div>
                  <div className="text-sm font-semibold text-red-800">Prioridad Alta</div>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-2 border-orange-200">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-orange-600">
                    {recommendations.filter(r => r.priority === 'medium').length}
                  </div>
                  <div className="text-sm font-semibold text-orange-800">Prioridad Media</div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-50 border-2 border-yellow-200">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-yellow-600">
                    {recommendations.filter(r => r.priority === 'low').length}
                  </div>
                  <div className="text-sm font-semibold text-yellow-800">Prioridad Baja</div>
                </CardContent>
              </Card>
            </div>

            {/* Lista de recomendaciones */}
            {recommendations.map((rec, idx) => (
              <Card
                key={idx}
                className={`border-2 ${
                  rec.priority === 'high' ? 'border-red-300 bg-red-50' :
                  rec.priority === 'medium' ? 'border-orange-300 bg-orange-50' :
                  'border-yellow-300 bg-yellow-50'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                          rec.priority === 'high' ? 'bg-red-200 text-red-800' :
                          rec.priority === 'medium' ? 'bg-orange-200 text-orange-800' :
                          'bg-yellow-200 text-yellow-800'
                        }`}>
                          {rec.priority === 'high' ? '🔴 ALTA' : rec.priority === 'medium' ? '🟠 MEDIA' : '🟡 BAJA'}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-semibold">
                          {rec.category}
                        </span>
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">{rec.title}</h4>
                      <p className="text-sm text-gray-700 mb-3">{rec.description}</p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <h5 className="text-sm font-semibold text-gray-800 mb-2">✅ Acciones recomendadas:</h5>
                    <ul className="space-y-1">
                      {rec.actions.map((action, actionIdx) => (
                        <li key={actionIdx} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {rec.examples && rec.examples.length > 0 && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                      <h5 className="text-sm font-semibold text-gray-800 mb-2">📝 Ejemplos detectados:</h5>
                      <div className="space-y-1">
                        {rec.examples.map((example, exIdx) => (
                          <div key={exIdx} className="text-xs text-gray-600 font-mono bg-gray-50 p-2 rounded">
                            {example}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Tab: Criterios de Lenguaje Claro
  function renderB2CriteriaTab() {
    if (!analysis.analysisMetadata?.b2Criteria) {
      return <div className="text-center text-gray-500 py-8">No hay datos de criterios de Lenguaje Claro disponibles</div>;
    }

    // Obtener todos los criterios
    const allCriteria = analysis.analysisMetadata.b2Criteria.criteria;

    // Clasificar criterios según los 4 principios de la norma UNE-ISO 24495-1:2024

    // 1. PERTINENCIA: ¿Es la información que el lector necesita?
    // Criterios estratégicos que validan si el documento responde al objetivo del lector
    // Actualmente no hay criterios implementados (todos serían manuales)
    const pertinenciaCriteria = allCriteria.filter((c: any) =>
      c.name.toLowerCase().includes('adecuación al público') ||
      c.name.toLowerCase().includes('propósito claro') ||
      c.name.toLowerCase().includes('suficiencia') ||
      c.name.toLowerCase().includes('superflua')
    );

    // 2. FACILIDAD DE BÚSQUEDA: ¿Se encuentra fácilmente la información?
    // Criterios sobre estructura, jerarquía visual y diseño del documento
    const facilidadBusquedaCriteria = allCriteria.filter((c: any) =>
      (c.name.toLowerCase().includes('estructura clara') && c.name.toLowerCase().includes('títulos')) ||
      c.name.toLowerCase().includes('listas') ||
      c.name.toLowerCase().includes('viñetas') ||
      c.name.toLowerCase().includes('ilustraciones') ||
      (c.name.toLowerCase().includes('gráficos') && c.name.toLowerCase().includes('iconos'))
    );

    // 3. COMPRENSIBILIDAD: ¿Se entiende el texto?
    // Criterios lingüísticos y de redacción (mayoría automáticos)
    const comprensibilidadCriteria = allCriteria.filter((c: any) => {
      // Excluir explícitamente los criterios que van en Facilidad de Búsqueda
      if (c.name.toLowerCase().includes('listas') ||
          c.name.toLowerCase().includes('viñetas') ||
          c.name.toLowerCase().includes('ilustraciones') ||
          c.name.toLowerCase().includes('gráficos') ||
          (c.name.toLowerCase().includes('iconos') && c.name.toLowerCase().includes('glosario'))) {
        return false;
      }

      return (
        ['legibilidad', 'vocabulario', 'claridad'].includes(c.category?.toLowerCase()) ||
        (c.category?.toLowerCase() === 'estructura' &&
         (c.name.toLowerCase().includes('voz activa') ||
          c.name.toLowerCase().includes('conectores') ||
          c.name.toLowerCase().includes('subordinadas'))) ||
        (c.category?.toLowerCase() === 'formato' && c.name.toLowerCase().includes('mayúsculas')) ||
        c.name.toLowerCase().includes('inflesz') ||
        c.name.toLowerCase().includes('glosario') ||
        c.name.toLowerCase().includes('negaciones') ||
        c.name.toLowerCase().includes('indeterminadas') ||
        c.name.toLowerCase().includes('tono y estilo')
      );
    });

    // 4. USABILIDAD: ¿Puede el lector usar la información para actuar?
    // Criterios que validan si el documento funciona en la práctica real
    const usabilidadCriteria = allCriteria.filter((c: any) =>
      c.name.toLowerCase().includes('validación') ||
      c.name.toLowerCase().includes('usuarios reales') ||
      c.name.toLowerCase().includes('orientación a la acción') ||
      c.name.toLowerCase().includes('siguientes pasos')
    );

    // Función para renderizar un criterio
    const renderCriterion = (criterion: any, index: number) => {
      const [valor, explicacion] = criterion.description?.split(' | ') || [criterion.description, ''];
      const isManual = !criterion.scientific;
      const criterionKey = `${criterion.name}-${index}`;

      // Determinar el estado de validación
      // Para criterios automáticos: usar su status inicial, permitir modificación
      // Para criterios manuales: iniciar como null (sin validar)
      const validation = criteriaValidations[criterionKey] !== undefined
        ? criteriaValidations[criterionKey]
        : (isManual ? null : criterion.status === 'pass');

      const handleValidationChange = (passes: boolean) => {
        const updatedValidations = {
          ...criteriaValidations,
          [criterionKey]: passes
        };
        setCriteriaValidations(updatedValidations);

        // Save to database
        saveValidationsMutation.mutate(updatedValidations);
      };

      return (
        <div
          key={index}
          className={`p-4 rounded-lg border-2 ${
            validation === true ? 'bg-green-50 border-green-200' :
            validation === false ? 'bg-red-50 border-red-200' :
            'bg-gray-50 border-gray-300'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">
                  {validation === true ? (
                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                  ) : validation === false ? (
                    <FontAwesomeIcon icon={faTimesCircle} className="text-red-600" />
                  ) : (
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-400" />
                  )}
                </span>
                <h4 className="font-semibold text-gray-900">{criterion.name}</h4>
                <span className="text-xs px-2 py-0.5 bg-gray-200 rounded-full text-gray-600">
                  {criterion.category}
                </span>
                {isManual && (
                  <span className="text-xs px-2 py-0.5 bg-amber-100 rounded-full text-amber-700">
                    manual
                  </span>
                )}
                {!isManual && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 rounded-full text-blue-700">
                    automático
                  </span>
                )}
                {criterion.weight && criterion.weight > 0 && criterion.scientific && (
                  <span className="text-xs px-2 py-0.5 bg-purple-100 rounded-full text-purple-700 font-semibold">
                    Pondera {criterion.weight}%
                  </span>
                )}
              </div>
              <div className="ml-7">
                <p className="text-sm font-medium text-gray-700">{valor}</p>
                {explicacion && (
                  <p className="text-xs text-gray-500 mt-1 italic">{explicacion}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 ml-4">
              <button
                onClick={() => handleValidationChange(true)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 transition-all ${
                  validation === true
                    ? 'bg-green-500 border-green-600 text-white shadow-md'
                    : 'bg-white border-green-300 text-green-700 hover:bg-green-50'
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="text-sm" />
                <span className="text-xs font-semibold">Pasa</span>
              </button>
              <button
                onClick={() => handleValidationChange(false)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 transition-all ${
                  validation === false
                    ? 'bg-red-500 border-red-600 text-white shadow-md'
                    : 'bg-white border-red-300 text-red-700 hover:bg-red-50'
                }`}
              >
                <FontAwesomeIcon icon={faTimesCircle} className="text-sm" />
                <span className="text-xs font-semibold">No Pasa</span>
              </button>
            </div>
          </div>
        </div>
      );
    };

    // Función para calcular si un principio pasa
    // Para Comprensibilidad usa pesos ponderados, para otros principios cuenta criterios
    const calculatePrincipleStatus = (criteria: any[], useWeights: boolean = false) => {
      if (criteria.length === 0) return { passes: false, percentage: 0, validated: 0, total: 0, score: 0, maxScore: 0 };

      if (useWeights) {
        // Modo ponderado: usa los pesos de cada criterio (para Comprensibilidad)
        let earnedScore = 0;
        let maxScore = 0;

        criteria.forEach((criterion, index) => {
          const criterionKey = `${criterion.name}-${index}`;
          const validation = criteriaValidations[criterionKey] !== undefined
            ? criteriaValidations[criterionKey]
            : (!criterion.scientific ? null : criterion.status === 'pass');

          const weight = criterion.weight || 0;
          maxScore += weight;

          if (validation === true) {
            earnedScore += weight;
          }
        });

        const percentage = maxScore > 0 ? (earnedScore / maxScore) * 100 : 0;
        return {
          passes: percentage >= 85,
          percentage: percentage,
          validated: Math.round(earnedScore),
          total: maxScore,
          score: earnedScore,
          maxScore: maxScore
        };
      } else {
        // Modo simple: cuenta criterios (para otros principios)
        let passedCount = 0;
        let totalCriteria = criteria.length;

        criteria.forEach((criterion, index) => {
          const criterionKey = `${criterion.name}-${index}`;
          const validation = criteriaValidations[criterionKey] !== undefined
            ? criteriaValidations[criterionKey]
            : (!criterion.scientific ? null : criterion.status === 'pass');

          if (validation === true) {
            passedCount++;
          }
        });

        const percentage = (passedCount / totalCriteria) * 100;
        return {
          passes: percentage >= 85,
          percentage: percentage,
          validated: passedCount,
          total: totalCriteria,
          score: passedCount,
          maxScore: totalCriteria
        };
      }
    };

    // Calcular estado de cada principio
    const pertinenciaStatus = calculatePrincipleStatus([
      ...([
        {
          name: 'Adecuación al Público Objetivo',
          category: 'pertinencia',
          description: 'Verificar que el documento se ha escrito pensando en el perfil del lector | Evaluar conocimientos previos, contexto sociocultural y necesidades específicas del público objetivo',
          scientific: false,
          status: null
        },
        {
          name: 'Propósito Claro',
          category: 'pertinencia',
          description: 'Evaluar si el documento responde directamente al objetivo del lector | El lector debe poder tomar una decisión, realizar un trámite o entender un concepto específico',
          scientific: false,
          status: null
        },
        {
          name: 'Suficiencia de la Información',
          category: 'pertinencia',
          description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo | Ni más ni menos: solo lo necesario para cumplir el propósito sin generar ruido informativo',
          scientific: false,
          status: null
        }
      ]),
      ...pertinenciaCriteria
    ], false); // Sin pesos

    const facilidadStatus = calculatePrincipleStatus(facilidadBusquedaCriteria, false); // Sin pesos
    const comprensibilidadStatus = calculatePrincipleStatus(comprensibilidadCriteria, true); // CON pesos ponderados
    const usabilidadStatus = calculatePrincipleStatus(usabilidadCriteria, false); // Sin pesos

    // Determinar si cumple con Lenguaje Claro (todos los principios deben pasar)
    const cumpleLenguajeClaro = pertinenciaStatus.passes && facilidadStatus.passes && comprensibilidadStatus.passes && usabilidadStatus.passes;

    return (
      <div className="space-y-6">
        {/* Header con validador de Lenguaje Claro */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Evaluación según UNE-ISO 24495-1:2024</h2>
              <p className="text-sm text-gray-600">
                Criterios organizados por los 4 principios de Lenguaje Claro
              </p>
            </div>
            <div className={`px-6 py-3 rounded-lg text-sm font-bold border-2 ${
              cumpleLenguajeClaro
                ? 'bg-green-100 text-green-800 border-green-400'
                : 'bg-red-100 text-red-800 border-red-400'
            }`}>
              {cumpleLenguajeClaro ? '✓ CUMPLE LENGUAJE CLARO' : '✗ NO CUMPLE LENGUAJE CLARO'}
            </div>
          </div>
        </div>

        {/* 1. PERTINENCIA - ¿Es la información que el lector necesita? */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                Principio 1. Pertinencia
              </h2>
              <div className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                pertinenciaStatus.passes
                  ? 'bg-green-100 text-green-800 border-2 border-green-300'
                  : 'bg-red-100 text-red-800 border-2 border-red-300'
              }`}>
                {pertinenciaStatus.passes ? '✓ PASA' : '✗ NO PASA'} ({pertinenciaStatus.validated}/{pertinenciaStatus.total} - {pertinenciaStatus.percentage.toFixed(0)}%)
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-2 font-semibold">
              ¿Es la información que el lector necesita?
            </p>
            <p className="text-sm text-gray-600 mb-4">
              El documento debe contener la información que el lector necesita (ni más, ni menos) para cumplir su objetivo.
              Se identifica el <strong>público objetivo</strong>, su <strong>contexto</strong> y el <strong>propósito de la lectura</strong>.
              Evita información superflua que genere ruido.
            </p>

            <div className="space-y-3">
              {/* Criterios manuales de Pertinencia */}
              {[
                {
                  name: 'Adecuación al Público Objetivo',
                  category: 'pertinencia',
                  description: 'Verificar que el documento se ha escrito pensando en el perfil del lector | Evaluar conocimientos previos, contexto sociocultural y necesidades específicas del público objetivo',
                  scientific: false,
                  status: null
                },
                {
                  name: 'Propósito Claro',
                  category: 'pertinencia',
                  description: 'Evaluar si el documento responde directamente al objetivo del lector | El lector debe poder tomar una decisión, realizar un trámite o entender un concepto específico',
                  scientific: false,
                  status: null
                },
                {
                  name: 'Suficiencia de la Información',
                  category: 'pertinencia',
                  description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo | Ni más ni menos: solo lo necesario para cumplir el propósito sin generar ruido informativo',
                  scientific: false,
                  status: null
                }
              ].map(renderCriterion)}

              {pertinenciaCriteria.map(renderCriterion)}
            </div>
          </CardContent>
        </Card>

        {/* 2. FACILIDAD DE BÚSQUEDA - ¿Se encuentra fácilmente la información? */}
        {facilidadBusquedaCriteria.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  Principio 2. Facilidad de Búsqueda (Localización)
                </h2>
                <div className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                  facilidadStatus.passes
                    ? 'bg-green-100 text-green-800 border-2 border-green-300'
                    : 'bg-red-100 text-red-800 border-2 border-red-300'
                }`}>
                  {facilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'} ({facilidadStatus.validated}/{facilidadStatus.total} - {facilidadStatus.percentage.toFixed(0)}%)
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2 font-semibold">
                ¿Se encuentra fácilmente la información?
              </p>
              <p className="text-sm text-gray-600 mb-4">
                La información debe estar <strong>organizada y estructurada</strong> de forma lógica mediante:
                títulos claros, subtítulos jerárquicos, listas con viñetas, uso estratégico de negritas,
                espaciado adecuado, y <strong>apoyos visuales</strong> (ilustraciones, gráficos, iconos)
                que faciliten la navegación visual del documento.
              </p>
              <div className="space-y-3">
                {facilidadBusquedaCriteria.map(renderCriterion)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3. COMPRENSIBILIDAD - ¿Se entiende el texto? */}
        {comprensibilidadCriteria.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  Principio 3. Comprensibilidad (Nivel B2 del MCERL)
                </h2>
                <div className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                  comprensibilidadStatus.passes
                    ? 'bg-green-100 text-green-800 border-2 border-green-300'
                    : 'bg-red-100 text-red-800 border-2 border-red-300'
                }`}>
                  {comprensibilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'} ({comprensibilidadStatus.validated}/{comprensibilidadStatus.total} - {comprensibilidadStatus.percentage.toFixed(0)}%)
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2 font-semibold">
                ¿Se entiende el texto?
              </p>
              <p className="text-sm text-gray-600 mb-4">
                El texto debe usar <strong>palabras comunes</strong> (A1-B2), evitar jerga técnica,
                escribir <strong>frases cortas y directas</strong> (15-20 palabras máximo) con una sola idea principal,
                priorizar <strong>voz activa</strong>, limitar subordinadas,
                usar conectores simples, evitar palabras vagas y negaciones dobles,
                y transmitir un <strong>tono respetuoso</strong> y cercano.
              </p>
              <div className="space-y-3">
                {comprensibilidadCriteria.map(renderCriterion)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detailed Analysis */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Passive Voice */}
          {analysis.analysisMetadata.b2Criteria.passiveVoice.count > 0 && (
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-semibold mb-2">Voz Pasiva Detectada</h4>
              <p className="text-sm text-gray-700 mb-2">
                {analysis.analysisMetadata.b2Criteria.passiveVoice.count} construcciones ({analysis.analysisMetadata.b2Criteria.passiveVoice.percentage.toFixed(1)}%)
              </p>
              {analysis.analysisMetadata.b2Criteria.passiveVoice.examples.length > 0 && (
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="font-semibold">Ejemplos:</div>
                  {analysis.analysisMetadata.b2Criteria.passiveVoice.examples.slice(0, 3).map((example: string, i: number) => (
                    <div key={i} className="italic">"{example}"</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Complex Connectors */}
          {analysis.analysisMetadata.b2Criteria.complexConnectors.count > 0 && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold mb-2">Conectores Complejos</h4>
              <p className="text-sm text-gray-700 mb-2">
                {analysis.analysisMetadata.b2Criteria.complexConnectors.count} tipos encontrados
              </p>
              <div className="text-xs text-gray-600 flex flex-wrap gap-1">
                {analysis.analysisMetadata.b2Criteria.complexConnectors.connectors.slice(0, 5).map((conn: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-purple-100 rounded">{conn}</span>
                ))}
              </div>
            </div>
          )}

          {/* Uppercase Words */}
          {analysis.analysisMetadata.b2Criteria.uppercaseWords.count > 0 && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold mb-2">Palabras en Mayúsculas</h4>
              <p className="text-sm text-gray-700 mb-2">
                {analysis.analysisMetadata.b2Criteria.uppercaseWords.count} palabras ({analysis.analysisMetadata.b2Criteria.uppercaseWords.percentage.toFixed(1)}%)
              </p>
              <div className="text-xs text-gray-600 flex flex-wrap gap-1">
                {analysis.analysisMetadata.b2Criteria.uppercaseWords.words.slice(0, 10).map((word: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-blue-100 rounded">{word}</span>
                ))}
              </div>
            </div>
          )}

          {/* Vague Words */}
          {analysis.analysisMetadata.b2Criteria.vagueWords.count > 0 && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h4 className="font-semibold mb-2">Palabras Indeterminadas</h4>
              <p className="text-sm text-gray-700 mb-2">
                {analysis.analysisMetadata.b2Criteria.vagueWords.count} palabras vagas
              </p>
              <div className="text-xs text-gray-600 flex flex-wrap gap-1">
                {analysis.analysisMetadata.b2Criteria.vagueWords.words.slice(0, 10).map((word: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-orange-100 rounded">{word}</span>
                ))}
              </div>
            </div>
          )}

          {/* Double Negations */}
          {analysis.analysisMetadata.b2Criteria.doubleNegations.count > 0 && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-semibold mb-2">Negaciones Dobles</h4>
              <p className="text-sm text-gray-700 mb-2">
                {analysis.analysisMetadata.b2Criteria.doubleNegations.count} construcciones detectadas
              </p>
              {analysis.analysisMetadata.b2Criteria.doubleNegations.examples.length > 0 && (
                <div className="text-xs text-gray-600 space-y-1">
                  {analysis.analysisMetadata.b2Criteria.doubleNegations.examples.slice(0, 3).map((example: string, i: number) => (
                    <div key={i} className="italic">"{example}"</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Subordinate Clauses */}
          {analysis.analysisMetadata.b2Criteria.subordinateClauses.count > 0 && (
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <h4 className="font-semibold mb-2">Oraciones Subordinadas</h4>
              <p className="text-sm text-gray-700 mb-2">
                {analysis.analysisMetadata.b2Criteria.subordinateClauses.count} subordinadas
                (promedio: {analysis.analysisMetadata.b2Criteria.subordinateClauses.avgPerSentence.toFixed(2)}/oración)
              </p>
              {analysis.analysisMetadata.b2Criteria.subordinateClauses.examples.length > 0 && (
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="font-semibold">Ejemplos:</div>
                  {analysis.analysisMetadata.b2Criteria.subordinateClauses.examples.slice(0, 2).map((example: string, i: number) => (
                    <div key={i} className="italic">"{example}"</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 4. USABILIDAD - ¿Puede el lector usar la información para actuar? */}
        {usabilidadCriteria.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  Principio 4. Usabilidad
                </h2>
                <div className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                  usabilidadStatus.passes
                    ? 'bg-green-100 text-green-800 border-2 border-green-300'
                    : 'bg-red-100 text-red-800 border-2 border-red-300'
                }`}>
                  {usabilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'} ({usabilidadStatus.validated}/{usabilidadStatus.total} - {usabilidadStatus.percentage.toFixed(0)}%)
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2 font-semibold">
                ¿Puede el lector usar la información para actuar?
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Este principio verifica que <strong>los tres anteriores han funcionado en la práctica</strong>.
                El documento debe permitir al lector <strong>usar la información</strong> para tomar decisiones,
                completar tareas o entender sus derechos y obligaciones.
                Se valida mediante <strong>pruebas con usuarios reales</strong> del público objetivo.
              </p>
              <div className="space-y-3">
                {usabilidadCriteria.map(renderCriterion)}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Tab: Legibilidad
  // @ts-expect-error - Function kept for potential future use
  function _renderReadabilityTab() {
    if (!analysis.analysisMetadata?.readability) {
      return <div className="text-center text-gray-500 py-8">No hay datos de legibilidad disponibles</div>;
    }

    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4">Fórmula de Legibilidad INFLESZ</h2>
          <p className="text-sm text-gray-600 mb-6">
            INFLESZ es la fórmula de legibilidad adaptada al español que calcula la complejidad textual.
            Escala: 0-40 (muy difícil), 40-55 (algo difícil), 55-65 (normal), 65-80 (fácil), 80-100 (muy fácil).
          </p>

          {/* INFLESZ */}
          <div className="bg-purple-50 p-6 rounded-lg border border-purple-200 max-w-2xl mx-auto">
            <h3 className="text-lg font-bold text-purple-900 mb-4">INFLESZ (Adaptación Española)</h3>

            <div className="bg-white p-4 rounded-lg mb-4">
              <div className="text-sm text-gray-600 mb-2"><strong>Fórmula:</strong></div>
              <div className="font-mono text-xs bg-gray-100 p-2 rounded">
                206.84 - (1.02 × P/F) - (60 × S/P)
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg mb-4">
              <div className="text-sm text-gray-600 mb-2"><strong>Donde:</strong></div>
              <ul className="text-xs space-y-1">
                <li>• P/F = Palabras por frase ({analysis.avgWordsPerSentence != null ? Number(analysis.avgWordsPerSentence).toFixed(1) : 'N/A'})</li>
                <li>• S/P = Sílabas por palabra ({analysis.avgSyllablesPerWord != null ? Number(analysis.avgSyllablesPerWord).toFixed(2) : 'N/A'})</li>
              </ul>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-2"><strong>Cálculo:</strong></div>
              <div className="font-mono text-xs bg-gray-100 p-2 rounded mb-2">
                206.84 - ({(Number(analysis.avgWordsPerSentence) * 1.02).toFixed(1)}) - ({(Number(analysis.avgSyllablesPerWord) * 60).toFixed(1)}) = {analysis.infleszScore != null ? Number(analysis.infleszScore).toFixed(1) : 'N/A'}
              </div>
              <div className="mt-3">
                <div className="text-xs font-semibold">Resultado: <span className="text-2xl text-purple-600">{analysis.infleszScore != null ? Number(analysis.infleszScore).toFixed(1) : 'N/A'} puntos</span></div>
                <div className="text-xs mt-1">Interpretación: <span className={`font-semibold ${Number(analysis.infleszScore) >= 55 ? 'text-green-600' : 'text-red-600'}`}>
                  {Number(analysis.infleszScore) >= 55 ? <><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Cumple (≥55 req.)</> : <><FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> No cumple (≥55 req.)</>}
                </span></div>
                <div className="text-xs mt-1"><FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-1" />Cobertura: 100% del vocabulario</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Tab: Vocabulario
  function renderVocabularyTab() {
    if (!analysis.analysisMetadata?.vocabularyDistribution) {
      return <div className="text-center text-gray-500 py-8">No hay datos de vocabulario disponibles</div>;
    }

    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4">Análisis del Vocabulario</h2>
          <p className="text-sm text-gray-600 mb-6">
            Evalúa cada palabra del texto clasificándola según los niveles MCERL (A1, A2, B1, B2, C1, C2). Identifica términos complejos, palabras desconocidas y proporciona estadísticas detalladas sobre la distribución léxica.
          </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-600 mb-3">Estadísticas Generales</h4>
            <div className="space-y-2 text-sm">
              <div><strong>Total de palabras analizadas:</strong> {analysis.totalWords || 0}</div>
              <div><strong>Nivel predominante:</strong> {
                Object.entries(analysis.analysisMetadata.vocabularyDistribution || {})
                  .sort((a, b) => (b[1] as number) - (a[1] as number))[0]?.[0] || 'A1'
              } ({
                Object.entries(analysis.analysisMetadata.vocabularyDistribution || {})
                  .sort((a, b) => (b[1] as number) - (a[1] as number))[0]?.[1]
                  ? ((Object.entries(analysis.analysisMetadata.vocabularyDistribution || {})
                  .sort((a, b) => (b[1] as number) - (a[1] as number))[0]?.[1]) as number).toFixed(1)
                  : '0'
              }%)</div>
              <div><strong>Complejidad léxica:</strong> {
                ((analysis.analysisMetadata.vocabularyDistribution.B2 || 0) +
                 (analysis.analysisMetadata.vocabularyDistribution.C1 || 0) +
                 (analysis.analysisMetadata.vocabularyDistribution.C2 || 0)).toFixed(1)
              }% (Muy Baja)</div>
              <div><strong>Palabras complejas:</strong> {analysis.analysisMetadata.vocabularyCounts?.B2 + analysis.analysisMetadata.vocabularyCounts?.C1 + analysis.analysisMetadata.vocabularyCounts?.C2 || 0} (5+ sílabas o C1/C2)</div>
              <div><strong>Cobertura del análisis:</strong> 100%</div>
              <div><strong>Variaciones morfológicas:</strong> {analysis.analysisMetadata.morphology?.variations?.length || 0}</div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-600 mb-3">Idoneidad para B2</h4>
            <div className="space-y-2 text-sm">
              <div><strong>Vocabulario A1-B2:</strong> {
                ((analysis.analysisMetadata.vocabularyDistribution.A1 || 0) +
                 (analysis.analysisMetadata.vocabularyDistribution.A2 || 0) +
                 (analysis.analysisMetadata.vocabularyDistribution.B1 || 0) +
                 (analysis.analysisMetadata.vocabularyDistribution.B2 || 0)).toFixed(1)
              }%</div>
              <div><strong>Vocabulario C1-C2:</strong> {
                ((analysis.analysisMetadata.vocabularyDistribution.C1 || 0) +
                 (analysis.analysisMetadata.vocabularyDistribution.C2 || 0)).toFixed(1)
              }%</div>
              <div className={`font-semibold ${
                ((analysis.analysisMetadata.vocabularyDistribution.A1 || 0) +
                 (analysis.analysisMetadata.vocabularyDistribution.A2 || 0) +
                 (analysis.analysisMetadata.vocabularyDistribution.B1 || 0) +
                 (analysis.analysisMetadata.vocabularyDistribution.B2 || 0)) >= 85
                  ? 'text-green-600' : 'text-orange-600'
              }`}>
                Evaluación: {
                  ((analysis.analysisMetadata.vocabularyDistribution.A1 || 0) +
                   (analysis.analysisMetadata.vocabularyDistribution.A2 || 0) +
                   (analysis.analysisMetadata.vocabularyDistribution.B1 || 0) +
                   (analysis.analysisMetadata.vocabularyDistribution.B2 || 0)) >= 85
                    ? <><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Apto para B2</>
                    : <><FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" /> Revisar vocabulario</>
                }
              </div>
            </div>
          </div>
        </div>

        {/* Words by Level */}
        {analysis.analysisMetadata.wordsByLevel && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(analysis.analysisMetadata.wordsByLevel).map(([level, words]: [string, any]) => {
              const colorClasses = {
                A1: 'bg-green-500',
                A2: 'bg-teal-500',
                B1: 'bg-yellow-500',
                B2: 'bg-orange-500',
                C1: 'bg-red-500',
                C2: 'bg-purple-500'
              };
              const percentage = analysis.analysisMetadata.vocabularyDistribution[level] || 0;

              return (
                <div key={level} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className={`text-center mb-2 px-3 py-1 rounded text-white ${colorClasses[level as keyof typeof colorClasses]}`}>
                    Nivel {level} ({percentage.toFixed(1)}%)
                  </div>
                  <div className="text-xs text-gray-600 max-h-40 overflow-y-auto">
                    {Array.isArray(words) && words.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {words.slice(0, 15).map((word: string, i: number) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-200 rounded text-xs">{word}</span>
                        ))}
                        {words.length > 15 && <span className="text-gray-400">... y {words.length - 15} más</span>}
                      </div>
                    ) : (
                      <div className="text-center text-gray-400">Sin palabras</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </CardContent>
      </Card>
    );
  }

  // Tab: Morfología
  // @ts-expect-error - Function kept for potential future use
  function _renderMorphologyTab() {
    if (!analysis.analysisMetadata?.morphology) {
      return <div className="text-center text-gray-500 py-8">No hay datos morfológicos disponibles</div>;
    }

    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4">Análisis Morfológico</h2>
          <p className="text-sm text-gray-600 mb-6">
            Examina la estructura interna de las palabras identificando raíces, prefijos, sufijos y desinencias. Analiza la complejidad morfológica, procesos de derivación y composición, conjugaciones verbales, y proporciona lematización para reducir palabras a su forma canónica.
          </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-600 mb-3">Estadísticas de Reconocimiento</h4>
            <div className="space-y-2 text-sm">
              <div><strong>Total de palabras:</strong> {analysis.analysisMetadata.morphology.totalWords || 0}</div>
              <div><strong>Reconocimiento directo:</strong> {analysis.analysisMetadata.morphology.directMatches || 0} ({((analysis.analysisMetadata.morphology.directMatches / analysis.analysisMetadata.morphology.totalWords) * 100).toFixed(0)}%)</div>
              <div><strong>Vía stemming:</strong> {analysis.analysisMetadata.morphology.stemmingMatches || 0} ({((analysis.analysisMetadata.morphology.stemmingMatches / analysis.analysisMetadata.morphology.totalWords) * 100).toFixed(0)}%)</div>
              <div><strong>Reconocimiento parcial:</strong> {analysis.analysisMetadata.morphology.partialMatches || 0} ({((analysis.analysisMetadata.morphology.partialMatches / analysis.analysisMetadata.morphology.totalWords) * 100).toFixed(0)}%)</div>
              <div><strong>No reconocidas:</strong> {analysis.analysisMetadata.morphology.unknownWords || 0} ({((analysis.analysisMetadata.morphology.unknownWords / analysis.analysisMetadata.morphology.totalWords) * 100).toFixed(0)}%)</div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-600 mb-3">Calidad del Análisis</h4>
            <div className="space-y-2 text-sm">
              <div><strong>Cobertura total:</strong> {analysis.analysisMetadata.morphology.coverage}%</div>
              <div><strong>Precisión morfológica:</strong> {
                (((analysis.analysisMetadata.morphology.directMatches + analysis.analysisMetadata.morphology.stemmingMatches) /
                  analysis.analysisMetadata.morphology.totalWords) * 100).toFixed(0)
              }%</div>
              <div><strong>Variaciones detectadas:</strong> {analysis.analysisMetadata.morphology.variations?.length || 0}</div>
              <div className="text-green-600 font-semibold">
                Evaluación: <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />Excelente cobertura
              </div>
            </div>
          </div>
        </div>

        {/* Morphological Variations */}
        {analysis.analysisMetadata.morphology.variations && analysis.analysisMetadata.morphology.variations.length > 0 && (
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-3">Variaciones Morfológicas Detectadas (primeras 20)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {analysis.analysisMetadata.morphology.variations.slice(0, 20).map((variation: any, i: number) => (
                <div key={i} className="bg-white p-2 rounded text-sm">
                  <span className="font-semibold">{variation.word}</span> → {variation.lemma}
                  <span className="text-xs text-gray-500 ml-2">({variation.level})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
          <h4 className="text-green-800 font-semibold mb-2">Mejoras del Análisis Morfológico</h4>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• <strong>Stemming automático:</strong> Reduce inflexiones verbales a su raíz</li>
            <li>• <strong>Detección de género y número:</strong> Reconoce variaciones masculino/femenino y singular/plural</li>
            <li>• <strong>Análisis parcial:</strong> Identifica palabras por raíces compartidas</li>
            <li>• <strong>Cobertura mejorada:</strong> Aumenta precisión del análisis MCERL</li>
          </ul>
        </div>
        </CardContent>
      </Card>
    );
  }

  // Tab: Glosario
  function renderGlossaryTab() {
    if (!analysis.analysisMetadata?.glossaryTerms) {
      return <div className="text-center text-gray-500 py-8">No hay datos de glosario disponibles</div>;
    }

    const { glossaryTerms, acronyms } = analysis.analysisMetadata;

    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4">Términos que Requieren Glosario</h2>
          <p className="text-sm text-gray-600 mb-6">
            Identifica palabras técnicas, especializadas o de alta complejidad (niveles C1-C2) y siglas que necesitan explicación
            para lectores con nivel básico-intermedio de comprensión. Las palabras de uso común son comprensibles y NO requieren glosario.
          </p>

          {/* Explicación de Criterios */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-indigo-900 mb-3">¿Qué términos requieren glosario?</h3>
            <ul className="text-sm text-indigo-800 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">1.</span>
                <span><strong>Palabras nivel C1:</strong> Vocabulario avanzado que supera el uso común</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">2.</span>
                <span><strong>Palabras nivel C2:</strong> Vocabulario muy especializado o técnico</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">3.</span>
                <span><strong>Siglas y abreviaturas:</strong> Términos en mayúsculas que no son de uso común (OMS, PYME, etc.)</span>
              </li>
            </ul>
            <div className="mt-3 pt-3 border-t border-indigo-200">
              <p className="text-xs text-indigo-700">
                <strong>Nota:</strong> Las palabras de uso común son apropiadas para la mayoría de lectores y NO requieren glosario.
                Solo se marcan términos C1, C2 y siglas especializadas.
              </p>
            </div>
          </div>

          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Términos</div>
              <div className="text-3xl font-bold text-purple-600">{glossaryTerms.count}</div>
            </div>

            {glossaryTerms.breakdown && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Palabras C1</div>
                  <div className="text-2xl font-bold text-red-600">{glossaryTerms.breakdown.c1Words}</div>
                </div>

                <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Palabras C2</div>
                  <div className="text-2xl font-bold text-pink-600">{glossaryTerms.breakdown.c2Words}</div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Siglas</div>
                  <div className="text-2xl font-bold text-blue-600">{glossaryTerms.breakdown.acronyms}</div>
                </div>
              </>
            )}
          </div>

          {glossaryTerms.count > 0 ? (
            <div className="space-y-6">
              {/* Siglas Detectadas */}
              {acronyms && acronyms.list && acronyms.list.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <span className="text-xl">🔤</span>
                    Siglas y Abreviaturas ({acronyms.list.length})
                  </h4>
                  <p className="text-sm text-blue-800 mb-3">
                    Términos en mayúsculas que requieren definición en el glosario
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {acronyms.list.map((acronym: string, i: number) => (
                      <span key={i} className="px-3 py-2 bg-blue-100 text-blue-900 rounded-lg text-sm font-semibold border border-blue-300">
                        {acronym}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Todos los Términos */}
              <div>
                <h4 className="font-semibold mb-3">Todos los Términos de Glosario ({glossaryTerms.count})</h4>
                <div className="flex flex-wrap gap-2">
                  {glossaryTerms.terms.map((term: string, i: number) => (
                    <span key={i} className="px-3 py-2 bg-orange-100 text-orange-900 rounded-lg text-sm font-medium border border-orange-200">
                      {term}
                    </span>
                  ))}
                </div>
              </div>

            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-green-800 font-semibold mb-2 flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckCircle} />
                ¡Excelente! No se encontraron términos que requieran glosario.
              </h4>
              <p className="text-sm text-green-700">
                El texto no contiene palabras C1/C2 ni siglas especializadas que requieran definición.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Tab: Texto Original
  // @ts-expect-error - Function kept for potential future use
  function _renderOriginalTextTab() {
    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4">Texto Original</h2>
          <div className="p-4 bg-gray-50 rounded-lg max-h-96 overflow-y-auto">
            <p className="whitespace-pre-wrap">{analysis.originalText}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Tab: Simplificación IA
  function renderAISimplificationTab() {
    const isProcessing = simplifyMutation.isPending || analyzeSimplifiedMutation.isPending;

    return (
      <div className="space-y-6">
        {/* Texto Original */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-bold mb-4 text-gray-800">Texto Original</h3>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
              <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">{analysis.originalText}</p>
            </div>
            <div className="mt-3 text-xs text-gray-500">
              {analysis.totalWords} palabras • {analysis.totalSentences} oraciones
            </div>
          </CardContent>
        </Card>

        {/* Botón de Simplificación */}
        {!simplificationResult && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6 text-center">
              <div className="mb-4">
                <FontAwesomeIcon icon={faRobot} className="text-5xl text-blue-600 mb-3" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Simplificar con Inteligencia Artificial</h3>
                <p className="text-sm text-gray-600">
                  Haz clic en el botón para simplificar este texto y analizar automáticamente la versión simplificada
                </p>
              </div>
              <Button
                onClick={handleSimplifyAndAnalyze}
                disabled={isProcessing}
                size="lg"
                className="flex items-center gap-2 mx-auto"
              >
                <FontAwesomeIcon icon={faRobot} />
                {isProcessing ? (
                  simplifyMutation.isPending ? 'Simplificando...' : 'Analizando...'
                ) : 'Simplificar y Analizar'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Texto Simplificado */}
        {simplificationResult && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Texto Simplificado</h3>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-600 text-white">
                    {simplificationResult.provider} • {simplificationResult.targetLevel}
                  </Badge>
                  <Button
                    onClick={handleSimplifyAndAnalyze}
                    disabled={isProcessing}
                    size="sm"
                    variant="outline"
                  >
                    <FontAwesomeIcon icon={faRobot} className="mr-2" />
                    Volver a simplificar
                  </Button>
                </div>
              </div>
              <div className="p-4 bg-white rounded-lg border border-green-300 max-h-96 overflow-y-auto">
                <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">{simplificationResult.simplifiedText}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Análisis del Texto Simplificado */}
        {simplifiedAnalysis && (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-6 text-gray-800">Resultados del Análisis del Texto Simplificado</h2>

              {/* Comparación de Adecuación a Lenguaje Claro */}
              {simplifiedAnalysis.analysisMetadata?.b2Criteria && analysis.analysisMetadata?.b2Criteria && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-red-50">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold mb-4 text-gray-800">Texto Original</h3>
                      <ProgressBar
                        value={analysis.analysisMetadata.b2Criteria.b2Score}
                        threshold={85}
                        label="Adecuación a Lenguaje Claro"
                        showPercentage={true}
                        height="lg"
                        showThresholdLine={true}
                      />
                    </CardContent>
                  </Card>

                  <Card className="bg-green-50">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold mb-4 text-gray-800">Texto Simplificado</h3>
                      <ProgressBar
                        value={simplifiedAnalysis.analysisMetadata.b2Criteria.b2Score}
                        threshold={85}
                        label="Adecuación a Lenguaje Claro"
                        showPercentage={true}
                        height="lg"
                        showThresholdLine={true}
                      />
                      {simplifiedAnalysis.analysisMetadata.b2Criteria.b2Score > analysis.analysisMetadata.b2Criteria.b2Score && (
                        <div className="mt-3 text-sm text-green-700 font-semibold">
                          <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                          Mejora de +{(simplifiedAnalysis.analysisMetadata.b2Criteria.b2Score - analysis.analysisMetadata.b2Criteria.b2Score).toFixed(1)}%
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {analyzeSimplifiedMutation.isPending && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-700">Analizando texto simplificado...</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-600 rounded-xl p-8 shadow-lg">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-white">{analysis.title}</h1>
              {/* Audit Status Badge */}
              <Badge className={`flex items-center gap-1.5 ${
                analysis.auditStatus === 'AUDITED'
                  ? 'bg-green-500/90 text-white border-green-400'
                  : 'bg-gray-400/90 text-white border-gray-300'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  analysis.auditStatus === 'AUDITED' ? 'bg-green-200' : 'bg-gray-200'
                }`}></span>
                {analysis.auditStatus === 'AUDITED' ? 'Auditado' : 'Sin Auditar'}
              </Badge>
            </div>
            <p className="text-white/90">
              Análisis realizado el {new Date(analysis.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {analysis.sector && (
              <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                {analysis.sector.icon} {analysis.sector.name}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Actions - Moved to top */}
      <div className="flex gap-4">
        <Button onClick={() => navigate('/analysis/text')} variant="outline">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver al historial
        </Button>
        <Button
          onClick={() => toggleAuditStatusMutation.mutate()}
          disabled={toggleAuditStatusMutation.isPending}
          className={analysis.auditStatus === 'AUDITED' ? 'bg-gray-500 hover:bg-gray-600' : 'bg-green-600 hover:bg-green-700'}
        >
          <FontAwesomeIcon
            icon={analysis.auditStatus === 'AUDITED' ? faTimesCircle : faCheckCircle}
            className="mr-2"
          />
          {toggleAuditStatusMutation.isPending
            ? 'Actualizando...'
            : analysis.auditStatus === 'AUDITED'
              ? 'Marcar como Sin Auditar'
              : 'Marcar como Auditado'
          }
        </Button>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={`grid w-full mb-6 ${preferencesData?.preferences?.enableAiSimplification ? 'grid-cols-6' : 'grid-cols-5'}`}>
              <TabsTrigger value="overview">Resumen</TabsTrigger>
              <TabsTrigger value="b2-criteria">Criterios</TabsTrigger>
              <TabsTrigger value="vocabulary">Corpus Léxico</TabsTrigger>
              <TabsTrigger value="glossary">Glosario</TabsTrigger>
              <TabsTrigger value="recommendations">Recomendaciones</TabsTrigger>
              {preferencesData?.preferences?.enableAiSimplification && (
                <TabsTrigger value="ai-simplification">Simplificación IA</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview">{renderOverviewTab()}</TabsContent>
            <TabsContent value="b2-criteria">{renderB2CriteriaTab()}</TabsContent>
            <TabsContent value="vocabulary">{renderVocabularyTab()}</TabsContent>
            <TabsContent value="glossary">{renderGlossaryTab()}</TabsContent>
            <TabsContent value="recommendations">{renderRecommendationsTab()}</TabsContent>
            {preferencesData?.preferences?.enableAiSimplification && (
              <TabsContent value="ai-simplification">{renderAISimplificationTab()}</TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Simplification Modal */}
      <SimplificationModal
        isOpen={showSimplificationModal}
        onClose={() => setShowSimplificationModal(false)}
        originalText={simplificationResult?.originalText || analysis.originalText}
        simplifiedText={simplificationResult?.simplifiedText}
        title={`Simplificación: ${analysis.title}`}
        isLoading={simplifyMutation.isPending}
        provider={simplificationResult?.provider}
        targetLevel={simplificationResult?.targetLevel}
      />
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: 'blue' | 'purple' | 'green';
}

// @ts-expect-error - Component kept for potential future use
function _MetricCard({ title, value, icon, color }: MetricCardProps) {
  const colorClasses = {
    blue: { bg: 'bg-blue-500', text: 'text-blue-600' },
    purple: { bg: 'bg-fuchsia-500', text: 'text-fuchsia-600' },
    green: { bg: 'bg-emerald-500', text: 'text-emerald-600' },
  };

  const classes = colorClasses[color];

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className={`w-12 h-12 ${classes.bg} rounded-full flex items-center justify-center text-white text-lg shrink-0`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground font-medium mb-1">{title}</p>
            <p className={`text-3xl font-bold ${classes.text}`}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
